import {getTestProps} from '@benchling/js-test-utils';
import {createStyles} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useEffect, useState} from 'react';

import FieldActionButtons from 'metadata/FieldActionButtons';
import SchemaPrefixInput from 'schemas/SchemaPrefixInput';
import {EMPTY_PREFIX} from 'studies/StudyConstants';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import FormInput from 'ui-library/bs/FormInput';
import RadioButtonGroup, {RadioButtonSelector} from 'ui-library/inputs/RadioButtonGroup';
import {SettingsLabel} from 'ui-library/SettingsField';
import TextValue from 'ui-library/TextValue';

import {
  AUTO_GENERATE_ID_LABEL,
  CHANGE_ID_GENERATION_MODE_MESSAGE,
  MANUAL_ID_LABEL,
  SETTINGS_GRID_COLUMN_GAP_PERCENT,
  SETTINGS_GRID_COLUMN_WIDTH_PERCENT,
} from './StudySchemaConstants';

const useIdGenerationModeMessageStyles = createStyles((theme) => ({
  root: {
    marginTop: theme.spacing.sm,
  },
}));

export interface StudySchemaIdGenerationSettingsProps {
  isBeingCreated: boolean;
  prefix: string;
  autoGenerateId: boolean;
  /**
   * Stages (create) or persists (edit) the ID mode + prefix together.
   * Edit callers return a Promise<boolean> resolving to whether the save succeeded;
   * handleComplete rolls the draft back only on an explicit `false`.
   * Create callers stage synchronously and return void (no save to fail).
   */
  onIdGenerationChange: (autoGenerateId: boolean, prefix: string) => void | Promise<boolean>;
  prefixDataTestAttrs?: DataTestAttrs;
}

// The ID mode and prefix live inside a single group so it's visually clear they are edited together.
// `GroupBox` spans two settings-grid columns; the inner columns line up with the grid above (see
// schemaMetadataColumn in StudySchemaMetadataSettings). In edit mode the box gets the `editableField`
// class so hovering anywhere in the group reveals the shared edit affordance and highlights the box.
function GroupBox({
  isBeingEdited,
  children,
  dataTestAttrs,
}: {
  isBeingEdited?: boolean;
  children: React.ReactNode;
  dataTestAttrs?: DataTestAttrs;
}): JSX.Element {
  const isEditable = isBeingEdited != null;
  return (
    <div
      className={classNames(css(styles.groupBox), {
        editableField: isEditable,
        'is-beingEdited': isBeingEdited,
      })}
      {...(dataTestAttrs != null ? getTestProps(dataTestAttrs) : {})}
    >
      <div className={css(styles.groupColumns)}>{children}</div>
    </div>
  );
}

function CreateModeIdGenerationSettings({
  prefix,
  autoGenerateId,
  onIdGenerationChange,
  prefixDataTestAttrs,
}: StudySchemaIdGenerationSettingsProps): JSX.Element {
  return (
    <GroupBox>
      <div className={css(styles.idColumn)}>
        <RadioButtonGroup<boolean>
          title="ID"
          options={[
            {label: AUTO_GENERATE_ID_LABEL, value: true},
            {label: MANUAL_ID_LABEL, value: false},
          ]}
          // Switching to manual clears the prefix; keep the current prefix when switching to auto.
          onChange={(value) => onIdGenerationChange(value, value ? prefix : EMPTY_PREFIX)}
          selectedValue={autoGenerateId}
          isVertical={true}
        />
      </div>
      <div className={css(styles.prefixColumn)}>
        <SchemaPrefixInput
          isBeingCreated={true}
          isRequired={autoGenerateId}
          isWritable={autoGenerateId}
          onPrefixChange={(newPrefix) => onIdGenerationChange(autoGenerateId, newPrefix)}
          prefix={prefix}
          dataTestAttrs={prefixDataTestAttrs}
        />
      </div>
    </GroupBox>
  );
}

function EditModeIdGenerationSettings({
  prefix,
  autoGenerateId,
  onIdGenerationChange,
  prefixDataTestAttrs,
}: StudySchemaIdGenerationSettingsProps): JSX.Element {
  const {classes: idGenerationModeMessageClasses} = useIdGenerationModeMessageStyles();
  const [isBeingEdited, setIsBeingEdited] = useState(false);
  const [draftAutoGenerateId, setDraftAutoGenerateId] = useState(autoGenerateId);
  const [draftPrefix, setDraftPrefix] = useState(prefix);

  useEffect(() => {
    if (!isBeingEdited) {
      setDraftAutoGenerateId(autoGenerateId);
      setDraftPrefix(prefix);
    }
  }, [autoGenerateId, prefix, isBeingEdited]);

  const handleEdit = (): void => {
    setDraftAutoGenerateId(autoGenerateId);
    setDraftPrefix(prefix);
    setIsBeingEdited(true);
  };

  const handleCancel = (): void => {
    setDraftAutoGenerateId(autoGenerateId);
    setDraftPrefix(prefix);
    setIsBeingEdited(false);
  };

  const handleComplete = async (): Promise<void> => {
    const nextPrefix = draftAutoGenerateId ? draftPrefix : EMPTY_PREFIX;
    // Only an explicit `false` (failed save) rolls the draft back; `undefined` (void) and `true` are success.
    const didSave = await onIdGenerationChange(draftAutoGenerateId, nextPrefix);
    if (didSave === false) {
      setDraftAutoGenerateId(autoGenerateId);
      setDraftPrefix(prefix);
    }
    setIsBeingEdited(false);
  };

  const isCompleteEnabled = !draftAutoGenerateId || draftPrefix !== '';

  return (
    <GroupBox
      isBeingEdited={isBeingEdited}
      dataTestAttrs={{component: 'StudySchemaIdGenerationSettings', element: 'root'}}
    >
      <div className={css(styles.idColumn)}>
        <SettingsLabel label="ID" isRequired={false} withBottomMargin={true} />
        {isBeingEdited ? (
          <RadioButtonSelector<boolean>
            options={[
              {label: AUTO_GENERATE_ID_LABEL, value: true},
              {label: MANUAL_ID_LABEL, value: false},
            ]}
            onChange={(value) => {
              setDraftAutoGenerateId(value);
              if (!value) {
                setDraftPrefix(EMPTY_PREFIX);
              }
            }}
            selectedValue={draftAutoGenerateId}
            isVertical={true}
          />
        ) : (
          <div
            className="editableField-text"
            {...getTestProps({component: 'StudySchemaIdGenerationSettings', element: 'idViewer'})}
          >
            <TextValue value={autoGenerateId ? AUTO_GENERATE_ID_LABEL : MANUAL_ID_LABEL} />
          </div>
        )}
        <div className={idGenerationModeMessageClasses.root}>
          <InlineBanner bannerType={InlineBannerType.INFO} text={CHANGE_ID_GENERATION_MODE_MESSAGE} />
        </div>
      </div>
      <div className={css(styles.prefixColumn)}>
        <SettingsLabel
          label="Prefix"
          isRequired={isBeingEdited && draftAutoGenerateId}
          withBottomMargin={true}
        />
        <div className={css(styles.prefixField)}>
          {isBeingEdited ? (
            <FormInput
              className="u-flex-grow"
              disabled={!draftAutoGenerateId}
              onChange={(e) => {
                setDraftPrefix(e.target.value);
              }}
              id="study-schema-id-prefix"
              type="text"
              value={draftPrefix}
              dataTestAttrs={prefixDataTestAttrs ?? {component: 'StudySchemaEditor', element: 'prefixInput'}}
            />
          ) : (
            <div
              className={classNames('editableField-text', css(styles.prefixViewer))}
              {...getTestProps({component: 'StudySchemaIdGenerationSettings', element: 'prefixViewer'})}
            >
              <TextValue value={prefix} />
            </div>
          )}
          <FieldActionButtons
            cancel={handleCancel}
            complete={handleComplete}
            edit={handleEdit}
            isCompleteEnabled={isCompleteEnabled}
            isEditing={isBeingEdited}
          />
        </div>
      </div>
    </GroupBox>
  );
}

export default function StudySchemaIdGenerationSettings(
  props: StudySchemaIdGenerationSettingsProps
): JSX.Element {
  if (props.isBeingCreated) {
    return <CreateModeIdGenerationSettings {...props} />;
  }
  return <EditModeIdGenerationSettings {...props} />;
}

// The group spans the first two settings-grid columns plus the gap between them.
const GROUP_BOX_WIDTH_PERCENT = 2 * SETTINGS_GRID_COLUMN_WIDTH_PERCENT + SETTINGS_GRID_COLUMN_GAP_PERCENT;

const styles = StyleSheet.create({
  // Spans the first two settings-grid columns (Name/Study type width) so the group aligns with the row above.
  // Neutralize the horizontal padding/negative margin that `.editableField` adds, otherwise it shrinks the
  // inner width and shifts the Prefix column a few px left of the grid line above it. Keep vertical padding
  // so the highlighted box still has breathing room.
  groupBox: {
    flex: `0 0 ${GROUP_BOX_WIDTH_PERCENT}%`,
    minWidth: 0,
    marginLeft: 0,
    paddingLeft: 0,
    paddingRight: 0,
  },
  // A three-track grid whose column/gap/column ratios mirror the parent settings grid, so the two inner
  // columns line up with the grid columns above regardless of the box width. The middle track is the gap.
  groupColumns: {
    display: 'grid',
    gridTemplateColumns: `${SETTINGS_GRID_COLUMN_WIDTH_PERCENT}fr ${SETTINGS_GRID_COLUMN_GAP_PERCENT}fr ${SETTINGS_GRID_COLUMN_WIDTH_PERCENT}fr`,
    alignItems: 'flex-start',
    width: '100%',
  },
  idColumn: {
    gridColumn: '1',
    minWidth: 0,
  },
  prefixColumn: {
    gridColumn: '3',
    minWidth: 0,
  },
  prefixField: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  prefixViewer: {
    flex: '1 1 auto',
    minWidth: 0,
  },
});
